import { createContext } from 'react';

import { Article, Issue, Style } from '@/constants';

/**
 * The issue/article *structure to render* -- split out of the old, single
 * `pages/issue/Issue.context.tsx` (improvements/07-template-architecture.md's
 * "views -> pages/widgets sort" left a real `widgets/ -> pages/` back-reference
 * here, since `widgets/issue-reader/*` needed this data but `IssueContext`
 * lived in `pages/issue/`, above `widgets/` in the FSD layer order).
 *
 * Deliberately narrow: pure domain data (the issue, its articles, the
 * currently-selected article/language, and the entity-level setters that
 * just change *which* piece of that data is selected) -- not reader-UI
 * chrome state (view mode, font size, favorites, audio player), which stays
 * in `widgets/issue-reader/model/ReaderStateContext.ts` instead, since that's
 * genuinely reader-widget state, not issue data. `pages/issue/Issue.tsx`
 * provides real values into both; `widgets/issue-reader/*` and
 * `entities/issue`'s own consumers only ever read this one downward.
 */
export const IssueContext = createContext<IssueContextProps>({
	language: 'en',
	issue: null,
	currentArticle: null,
	stylesMap: new Map(),
	setCurrentArticle: () => {},
	articles: [],
	articlesMap: undefined,
	languages: new Set<string>(),
	articleLanguages: new Set<string>(),
	disabledLanguages: new Set<string>(),
	setLanguage: () => {},
	navigateToArticle: undefined,
});

export interface IssueContextProps {
	language: string;
	issue: Issue | null;
	currentArticle: Article | null;
	stylesMap: Map<string, Style>;
	articles: Array<Article>;
	setCurrentArticle: (article: Article) => void;
	articlesMap: Map<string, Article> | undefined;
	languages: Set<string>;
	articleLanguages: Set<string>;
	disabledLanguages: Set<string>;
	setLanguage: (lang: string) => void;
	navigateToArticle?: (articleId: number) => void;
}
